<template>
  <div class="transaction-table position-relative">
    <t-loader :loading="isBusy"></t-loader>
    <div class="row">
      <div class="col-12">
        <div class="card mt-2">
          <div class="card-header d-flex flex-wrap justify-content-between">
            <div>
              Transactions
              <br>
              <small class="text-capitalize">{{ this.filter.scope || 'All' }} transactions</small>
            </div>
            <div class="d-flex align-items-center flex-wrap">
              <div role="group" class="form-group mb-0 mr-3">
                <b-input-group size="sm">
                  <b-form-input
                      id="filter-input"
                      v-model="filter.search"
                      type="search"
                      placeholder="Type to Search"
                      debounce="500"
                      style="width: 15rem"
                  ></b-form-input>

                  <b-input-group-append>
                    <b-button :disabled="!filter.search" @click="filter.search = ''">Clear</b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>
              <b-form-group class="mb-0">
                <b-form-radio-group size="sm" button-variant="outline-secondary" id="scope" v-model="filter.scope"
                                    name="radios-btn-default" buttons>
                  <b-form-radio v-if="!(option.value === 'all' && (counts.pending === 0 || counts.handled === 0))"
                                v-for="option in scopes" :value="option.value" :key="option.value">
                    {{ option.text }}
                    <span v-if="counts[option.value] !== null">({{ counts[option.value] }})</span>
                  </b-form-radio>
                </b-form-radio-group>
              </b-form-group>
            </div>
          </div>
          <div class="card-body">
            <b-table
                show-empty
                :responsive="true"
                :items="transactionProvider"
                :busy.sync="isBusy"
                :fields="fields"
                :current-page="currentPage"
                :per-page="perPage"
                ref="table"
                :filter="filter"
                selectable
                select-mode="range"
                @row-selected="onRowSelected"
            >
              <template v-slot:head(selected)="header">
                <div class="form-check" v-if="selectableRows.length > 0">
                  <div class="checkbox-container">
                    <i class="material-icons" v-if="allRowsSelected" @click.prevent="header.clearSelected()">check</i>
                    <i class="material-icons" v-else @click.prevent="header.selectAllRows()"></i>
                  </div>
                </div>
              </template>
              <template v-slot:cell(selected)="data">
                <a href="#" class="form-check" v-if="data.item.status === 'pending'" @click="toggleRow(data)">
                  <div class="checkbox-container">
                    <i class="material-icons" v-show="data.rowSelected">check</i>
                  </div>
                </a>
              </template>
              <template v-slot:cell(status)="data">
                <span class="badge badge-pill text-capitalize" :class="'badge-'+data.item.class">{{
                    data.item.status
                  }}</span>
              </template>
              <template v-slot:cell(user.name)="data">
                <a class="text-info" :href="data.item.user.url">
                  {{ data.item.user.name }}
                </a>
              </template>
              <template v-slot:cell(total)="data">
                {{ data.item.money | formatMoney }}
              </template>
              <template v-slot:cell(serviceFee)="data">
                {{ data.item.serviceFee }} %
              </template>
              <template v-slot:cell(sendingAt)="data">
                {{ formatDateTime(data.item.sendingAt) }}
              </template>
              <template v-slot:cell(createdAt)="data">
                {{ formatDateTime(data.item.createdAt) }}
              </template>
              <template v-slot:head(prosperityId)="data">
                <span v-b-tooltip.hover title="Prosperity Transaction ID">Wallet ID</span>
              </template>
              <template v-slot:cell(prosperityId)="data">
                <a v-if="data.item.prosperityUrl" class="text-warning" :href="data.item.prosperityUrl">
                  {{ data.item.prosperityId }}
                </a>
                <template v-else>{{ data.item.prosperityId }}</template>
              </template>
              <template v-slot:cell(sourceName)="data">
                <a class="text-info d-block" :href="data.item.sourceUrl">{{ data.item.sourceName }}</a>
                <a v-if="data.item.sourcePopup === true" href="#" @click="viewSourceInfo(data.item)"
                   class="text-success">
                  {{ data.item.sourceDescription }}
                </a>
              </template>
            </b-table>
            <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-6">
        <div class="card">
          <div class="card-header">
            Send selected transactions
          </div>
          <div class="card-body">
            <p class="card-text">The selected transactions will be sent to the wallet and marked as handled if
              successful.</p>
            <div class="row">
              <div class="col-12">
                <confirm-toggle v-model="accepted" :label="toggleLabel"></confirm-toggle>
              </div>
              <div class="col-12">
                <button type="submit" class="btn btn-primary btn-block" @click.prevent="send"
                        :disabled="!accepted || selected.length < 1">Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal size="xl" hide-header hide-header-close hide-footer body-class="p-0" id="source-info">
      <template #default="{ hide }">
        <button type="button" data-dismiss="modal" aria-label="Close" class="close position-absolute"
                style="top: 0; right: 0; z-index: 2; padding: 1rem;" @click="hide">
          <span aria-hidden="true">×</span>
        </button>
        <template v-if="modalSelection && modalSelection.withdrawal_id !== null">
          <streamlabs-table class="mb-0" :withdraw-id="modalSelection.withdrawalId" :as-admin="true"
                            :title="`Streamlabs donations in Transaction #${modalSelection.id}`"></streamlabs-table>
        </template>
        <template v-else>
          No data available.
        </template>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import get from 'lodash/get';

export default {
  name: 'AdminTransactionTable',
  data() {
    return {
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      transactions: [],
      selected: [],
      modalSelection: null,
      isBusy: false,
      filter: {
        search: '',
        scope: 'pending',
      },
      locked: false,
      toggleLabel: 'I have selected the correct transactions',
      accepted: false,
      scopes: [
        {text: 'Pending', value: 'pending'},
        {text: 'Handled', value: 'handled'},
        {text: 'Show all', value: 'all'},
      ],
      counts: {
        all: null,
        pending: null,
        handled: null,
      },
      defaultFields: [
        {key: 'selected', label: 'Selected'},
        {key: 'id', label: '#'},
        {key: 'status', label: 'Status'},
        {key: 'service', label: 'Service'},
        {key: 'sourceName', label: 'Source'},
        {key: 'user.name', label: 'Name'},
        {key: 'total', label: 'Total'},
        {key: 'serviceFee', label: 'Charge Fee'},
        {key: 'message', label: 'Message'},
        {key: 'prosperityId', label: 'Wallet ID'},
        {key: 'sendingAt', label: 'Sending at'},
        {key: 'createdAt', label: 'Created at'},
        {key: 'user.email', label: 'Email'},
      ],
    };
  },
  computed: {
    fields() {
      return this.defaultFields;
    },
    selectableRows() {
      return this.transactions.filter(item => item.status === 'pending');
    },
    allRowsSelected() {
      let selectable = this.selectableRows;
      return (this.selected.length === selectable.length) && (selectable.length > 0);
    },
  },
  watch: {
    filter(newFilter, oldFilter) {
      if (newFilter !== oldFilter) {
        // Current page is invalid when filter is applied
        this.currentPage = 1;
      }
    },
  },
  methods: {
    viewSourceInfo(item) {
      this.modalSelection = item;
      if (this.modalSelection) {
        this.$bvModal.show('source-info');
      }
    },
    toggleRow(data) {
      const ref = this.$refs.table;
      // to select all use tableRef.selectAllRows()
      // to see all availabe table properties just do a console.log(tableRef)
      if (data.rowSelected === false) {
        ref.selectRow(data.index);
        return;
      }
      ref.unselectRow(data.index);
    },
    timeSinceTimestamp(ts) {
      return moment(ts).fromNow();
    },
    formatDateTime(ts) {
      return moment(ts).format('YYYY-MM-DD hh:mm');
    },
    transactionProvider(ctx) {
      const url = `/admin/services/transactions/resource?page=${ctx.currentPage}&size=${ctx.perPage}&filter=${ctx.filter.search}&scope=${ctx.filter.scope}`;
      let promise = axios.get(url);

      return promise.then((response) => {
        const data = response.data.data;
        const meta = response.data.meta;
        this.currentPage = meta.current_page;
        this.totalRows = meta.total;
        this.transactions = data || [];
        this.counts = response.data.counts || this.counts;

        return this.transactions;
      }).catch(error => {
        return [];
      });
    },
    send() {
      if (this.locked) {
        return;
      }
      this.lock();
      this.isBusy = true;

      axios.patch('/admin/services/transactions', {
        transactions: this.selected,
      }).then(response => {
        const count = response.data.length;
        if (count > 0) {
          this.$toasted.success(`${count} transactions sent.`);
        } else {
          this.$toasted.info(`No transactions sent.`);
        }
      }).catch(error => {
        const status = get(error, 'response.status');
        let message = 'Error sending transactions.';
        if (422 === status) {
          message = get(error, 'response.data.message', message);
        }
        this.$toasted.error(message);
      }).finally(() => {
        this.loaded();
        this.unlock();
      });
    },
    loaded() {
      this.isBusy = false;
      this.$nextTick(() => {
        this.$refs.table.refresh();
      });
    },
    onRowSelected(items) {
      this.selected = items.filter(item => item.status === 'pending').map(item => ({id: item.id}));
    },
    lock() {
      this.locked = true;
    },
    unlock() {
      this.locked = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../sass/_variables.scss";

.card-header .form-group .form-control {
  font-size: revert;

  &:focus {
    box-shadow: none;
  }
}

.transaction-table ::v-deep {
  table {
    ion-icon {
      vertical-align: middle;
      font-size: 24px;
    }

    &.table {
      thead, tbody {
        tr {
          th {
            &:first-child {
              width: 50px;
            }
          }

          th, td {
            vertical-align: middle;
          }

          &.table-active:hover td {
            color: $table-content-text-color;
          }

          &:focus, &:focus-visible {
            outline: none;
          }

          &.b-table-row-selected {
            cursor: auto;
          }
        }
      }
    }

    .form-check {
      padding-left: 0;

      .checkbox-container {
        cursor: pointer;
        background: white;
        width: 25px;
        height: 25px;
        transition: background-color .2s ease;

        &:hover {
          background: lightblue;
        }

        i {
          color: $table-content-text-color;
          width: 100%;
          height: 100%;
        }
      }
    }
  }
}
</style>
